import { rivalBeside, rivalsAround } from "@healthos/shared";
export function ordinal(n) {
    const teens = n % 100;
    if (teens >= 11 && teens <= 13)
        return `${n}th`;
    return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}
/** The person one rank above, when the board's loaded page reaches them. */
function aheadOf(me, entries) {
    const above = entries.find((e) => e.rank === me.rank - 1);
    return above && above.score > me.score ? above : null;
}
export function challengeStanding(input) {
    const { myEntry, entries, participantCount, others } = input;
    const label = "Where you stand";
    if (!myEntry || participantCount <= 1) {
        // No ranked standing to state. The roster is still worth a line, and a solo
        // route says so rather than leaving the cell empty.
        if (others > 0) {
            return {
                label,
                value: `Walking with ${others} ${others === 1 ? "other" : "others"}`,
                detail: null,
            };
        }
        return { label, value: "Just you so far", detail: "No one else has joined yet" };
    }
    const value = `${ordinal(myEntry.rank)} of ${participantCount}`;
    if (myEntry.rank === 1)
        return { label, value, detail: "You are out in front" };
    const above = aheadOf(myEntry, entries);
    // Paged boards do not always carry the row above, and a gap we cannot count
    // is not a gap we guess at.
    if (!above)
        return { label, value, detail: null };
    const gap = above.score - myEntry.score;
    return { label, value, detail: `${gap.toLocaleString()} steps behind ${above.displayName}` };
}
/** The overview card is the whole challenge, not the week the leaderboard opens on. */
function challengePositionPeriod(board) {
    if (board.scope === "route" || board.periodKind === "route" || board.periodKind === "final") {
        return "Total challenge";
    }
    return board.period;
}
function gapSentence(gap) {
    if (gap.steps === 0)
        return `Level with ${gap.name}`;
    const unit = gap.steps === 1 ? "step" : "steps";
    const way = gap.direction === "ahead" ? "ahead of" : "behind";
    return `${gap.steps.toLocaleString()} ${unit} ${way} ${gap.name}`;
}
/** "James Murphy" as "James M.", and a single word untouched. */
export function shortDisplayName(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0];
    if (!first)
        return name;
    const last = parts.length > 1 ? parts[parts.length - 1] : null;
    return last ? `${first} ${last.charAt(0).toUpperCase()}.` : first;
}
/**
 * A comfortable walking cadence, in steps per minute. The one number behind
 * "about 15 minutes of walking": a round figure for a brisk-but-ordinary pace,
 * not a measurement of this member.
 */
export const WALKING_STEPS_PER_MINUTE = 100;
/** Roughly how long a given step gap takes to walk, never under a minute. */
export function walkingMinutesForSteps(steps) {
    return Math.max(1, Math.round(steps / WALKING_STEPS_PER_MINUTE));
}
function neighbourFromRival(rival, myScore) {
    // The rival carries the gap, not their total. Ahead means they have more.
    const score = rival.direction === "behind" ? myScore + rival.steps : myScore - rival.steps;
    return {
        rank: rival.rank,
        place: ordinal(rival.rank),
        name: rival.displayName,
        shortName: shortDisplayName(rival.displayName),
        score: Math.max(0, score),
        steps: rival.steps,
        initials: rival.initials ?? null,
        avatarUrl: rival.avatarUrl ?? null,
        avatarColor: rival.avatarColor ?? null,
    };
}
function gapFromRival(rival) {
    return {
        direction: rival.direction,
        steps: rival.steps,
        name: rival.displayName,
        place: ordinal(rival.rank),
        initials: rival.initials ?? null,
        avatarUrl: rival.avatarUrl ?? null,
        avatarColor: rival.avatarColor ?? null,
    };
}
/**
 * The standing worth a card on the challenge overview.
 *
 * Null when the member is not ranked, or when they are the only person on the
 * board. "1st of 1" is not a position anyone won, and a missing row is not a
 * rank to invent.
 */
export function memberChallengePosition(board) {
    if (!board?.myEntry || board.participantCount <= 1)
        return null;
    const rival = board.nearestRival ?? rivalBeside(board.myEntry, board.entries);
    const gap = rival ? gapFromRival(rival) : null;
    // The board's own rival is counted from every row, so it wins its side. The
    // loaded page fills the other side only when the member's own row is on it:
    // a page that stops above them says nothing about who is beside them.
    const onPage = board.entries.some((entry) => entry.rank === board.myEntry?.rank);
    const around = onPage ? rivalsAround(board.myEntry, board.entries) : null;
    const aheadRival = (rival?.direction === "behind" ? rival : null) ?? around?.ahead ?? null;
    const behindRival = (rival?.direction === "ahead" ? rival : null) ?? around?.behind ?? null;
    const standing = challengeStanding({
        myEntry: board.myEntry,
        entries: board.entries,
        participantCount: board.participantCount,
        others: Math.max(0, board.participantCount - 1),
    });
    return {
        place: ordinal(board.myEntry.rank),
        rank: board.myEntry.rank,
        participantCount: board.participantCount,
        me: {
            name: board.myEntry.displayName,
            initials: board.myEntry.initials ?? null,
            avatarUrl: board.myEntry.avatarUrl ?? null,
            avatarColor: board.myEntry.avatarColor ?? null,
        },
        isFirst: board.myEntry.rank === 1,
        isLast: board.myEntry.rank >= board.participantCount,
        ahead: aheadRival ? neighbourFromRival(aheadRival, board.myEntry.score) : null,
        behind: behindRival ? neighbourFromRival(behindRival, board.myEntry.score) : null,
        gap,
        detail: gap ? gapSentence(gap) : standing.detail,
        period: challengePositionPeriod(board),
        score: board.myEntry.score,
    };
}
/**
 * The person the member is chasing, Apple and Fitbit style: the one place
 * above, or the one place below once they lead. Null when nobody is known on
 * that side, which the card reads as "nothing to say yet" rather than a guess.
 */
export function positionTarget(position) {
    const rival = position.isFirst ? position.behind : position.ahead;
    if (!rival)
        return null;
    const steps = `${rival.steps.toLocaleString()} ${rival.steps === 1 ? "step" : "steps"}`;
    const walk = `About ${minutesPhrase(walkingMinutesForSteps(rival.steps))} of walking.`;
    if (position.isFirst) {
        return {
            rival,
            fraction: 1,
            sentence: rival.steps === 0
                ? `${endSentence(`Level with ${rival.shortName}`)} One more step keeps you in front.`
                : `${endSentence(`${steps} ahead of ${rival.shortName}`)} ${walk}`,
        };
    }
    return {
        rival,
        fraction: rival.score > 0 ? Math.min(1, position.score / rival.score) : 1,
        sentence: rival.steps === 0
            ? `${endSentence(`Level with ${rival.shortName}`)} One more step moves you into ${rival.place}.`
            : `${steps} to move into ${rival.place}. ${walk}`,
    };
}
/** Closes a sentence without doubling the stop after "James M.". */
function endSentence(text) {
    return text.endsWith(".") ? text : `${text}.`;
}
function minutesPhrase(minutes) {
    if (minutes < 60)
        return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    const hours = Math.round(minutes / 60);
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
}
//# sourceMappingURL=challenge-standing.js.map